import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { CheckCircle2, Loader2, Download, Home } from "lucide-react";

const MAX_POLL_ATTEMPTS = 12; // ~24s at 2s interval
const isDev = process.env.NODE_ENV === "development";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { t, lang } = useI18n();
  const [state, setState] = useState({ status: "polling", payment_status: "pending" });
  const [receipt, setReceipt] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled || attempts.current >= MAX_POLL_ATTEMPTS) return;
      attempts.current += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setState(r.data);
        if (r.data.payment_status === "paid" && r.data.donation_id) {
          try {
            const list = await api.get("/admin/donations").catch(() => null);
            if (list?.data) {
              const found = list.data.find(d => d.id === r.data.donation_id);
              if (found?.receipt_token) setReceipt(found.receipt_token);
            }
          } catch (fetchErr) { if (isDev) console.warn("Receipt fetch failed:", fetchErr); }
          return;
        }
        if (r.data.payment_status === "pending") setTimeout(poll, 2000);
      } catch (e) { if (isDev) console.error(e); }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const paid = state.payment_status === "paid";

  const msgs = {
    fr: { title: "Merci !", sub: "Votre don a été encaissé avec succès.", polling: "Vérification du paiement en cours…", check: "Un email de confirmation avec votre reçu fiscal PDF vient de vous être envoyé.", home: "Retour à l'accueil", download: "Télécharger le reçu (PDF)", amount: "Montant" },
    en: { title: "Thank you!", sub: "Your donation was successfully processed.", polling: "Verifying payment…", check: "A confirmation email with your PDF tax receipt has been sent to you.", home: "Back to home", download: "Download receipt (PDF)", amount: "Amount" },
    de: { title: "Danke!", sub: "Ihre Spende wurde erfolgreich verarbeitet.", polling: "Zahlung wird überprüft…", check: "Eine Bestätigungs-E-Mail mit Ihrer PDF-Spendenbescheinigung wurde versandt.", home: "Zur Startseite", download: "Beleg herunterladen (PDF)", amount: "Betrag" },
  };
  const m = msgs[lang] || msgs.fr;

  return (
    <div className="min-h-screen bg-ivory grid place-items-center px-5" data-testid="payment-success">
      <div className="max-w-lg w-full bg-white rounded-3xl p-10 text-center shadow-xl border-t-4 border-gold">
        {paid ? (
          <>
            <CheckCircle2 className="mx-auto text-green-soft" size={72}/>
            <h1 className="font-display text-4xl text-deep-blue mt-4">{m.title}</h1>
            <p className="text-deep-blue/70 mt-3">{m.sub}</p>
            {state.amount && (
              <div className="mt-6 p-5 rounded-2xl bg-pearl">
                <div className="text-xs uppercase tracking-widest text-warm-orange">{m.amount}</div>
                <div className="font-display text-4xl text-gold mt-1">{state.amount.toFixed(2)} €</div>
              </div>
            )}
            <p className="text-sm text-deep-blue/60 mt-6">{m.check}</p>
            {receipt && (
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/receipts/${receipt}`} target="_blank" rel="noopener noreferrer" className="btn-gold mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold" data-testid="download-receipt">
                <Download size={18}/> {m.download}
              </a>
            )}
            <Link to="/" className="mt-3 inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-deep-blue text-deep-blue font-semibold" data-testid="back-home">
              <Home size={18}/> {m.home}
            </Link>
          </>
        ) : (
          <>
            <Loader2 className="mx-auto text-gold animate-spin" size={72}/>
            <h1 className="font-display text-3xl text-deep-blue mt-4">{m.polling}</h1>
            <p className="text-deep-blue/60 mt-2 text-sm font-mono">{sessionId?.slice(0, 24)}…</p>
          </>
        )}
      </div>
    </div>
  );
}
